import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useClickSound } from '../hooks/useClickSound';
import {
  createProject,
  updateProject,
  publishProject,
  unpublishProject,
  addProjectMedia,
  deleteProjectMedia,
  deleteProject,
  getProjectVerificationUrl,
  verifyProjectX,
  getAchievements,
  createAchievement,
  updateAchievement,
  deleteAchievement,
} from '../utils/api';
import MediaUploader from './MediaUploader';
import { useAuth } from '../hooks/useAuth';
import type { Project, ProjectMedia, MediaType, Achievement, AchievementType, Superteam } from '../types';

interface ProjectCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  editProject?: Project | null;
  superteams?: Superteam[];
}

export default function ProjectCreator({ isOpen, onClose, editProject, superteams = [] }: ProjectCreatorProps) {
  const { withClickSound } = useClickSound();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'media' | 'paper' | 'links' | 'achievements'>('details');
  
  // Form state
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [paperContent, setPaperContent] = useState('');
  const [website, setWebsite] = useState('');
  const [github, setGithub] = useState('');
  const [twitter, setTwitter] = useState('');
  const [discord, setDiscord] = useState('');
  const [logo, setLogo] = useState<ProjectMedia | null>(null);
  const [banner, setBanner] = useState<ProjectMedia | null>(null);
  const [media, setMedia] = useState<ProjectMedia[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [verifyingX, setVerifyingX] = useState(false);
  const [xVerifiedAt, setXVerifiedAt] = useState<string | undefined>(undefined);
  const [xUsername, setXUsername] = useState<string | undefined>(undefined);

  // Achievements state
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [achievementsFetched, setAchievementsFetched] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [achievementForm, setAchievementForm] = useState({
    type: 'hackathon_winner' as AchievementType,
    title: '',
    description: '',
    date: '',
    issuer: '',
    proofUrl: '',
    superteamId: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editProject;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (editProject) {
      setName(editProject.name);
      setTagline(editProject.tagline || '');
      setDescription(editProject.description || '');
      setPaperContent(editProject.paperContent || '');
      setWebsite(editProject.website || '');
      setGithub(editProject.github || '');
      setTwitter(editProject.twitter || '');
      setDiscord(editProject.discord || '');
      setLogo(editProject.logo || null);
      setBanner(editProject.banner || null);
      setMedia(editProject.media || []);
      setProjectId(editProject.id);
      setStatus(editProject.status);
      setXVerifiedAt(editProject.xVerifiedAt);
      setXUsername(editProject.xUsername);
    } else {
      // Reset form
      setName('');
      setTagline('');
      setDescription('');
      setPaperContent('');
      setWebsite('');
      setGithub('');
      setTwitter('');
      setDiscord('');
      setLogo(null);
      setBanner(null);
      setMedia([]);
      setProjectId(null);
      setStatus('draft');
      setXVerifiedAt(undefined);
      setXUsername(undefined);
    }
  }, [editProject]);

  // Fetch achievements when achievements tab is first opened
  useEffect(() => {
    if (activeTab === 'achievements' && user && !loadingAchievements && !achievementsFetched) {
      setLoadingAchievements(true);
      getAchievements(user.id)
        .then(response => {
          if (response.success && response.data) {
            setAchievements(response.data.achievements);
            setAchievementsFetched(true);
          }
        })
        .catch(err => {
          console.error('Failed to fetch achievements:', err);
          setError('Failed to load achievements');
        })
        .finally(() => setLoadingAchievements(false));
    }
  }, [activeTab, user?.id, loadingAchievements, achievementsFetched]);

  // Reset fetched flag when tab changes away from achievements
  useEffect(() => {
    if (activeTab !== 'achievements') {
      setAchievementsFetched(false);
    }
  }, [activeTab]);

  const handleSave = async (publish = false) => {
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let savedProjectId = projectId;

      if (isEditing && projectId) {
        // Update existing project
        await updateProject(projectId, {
          name: name.trim(),
          tagline: tagline.trim() || undefined,
          description: description.trim() || undefined,
          paperContent: paperContent.trim() || undefined,
          website: website.trim() || undefined,
          github: github.trim() || undefined,
          twitter: twitter.trim() || undefined,
          discord: discord.trim() || undefined,
        });
      } else {
        // Create new project
        const response = await createProject({
          name: name.trim(),
          tagline: tagline.trim() || undefined,
          description: description.trim() || undefined,
          paperContent: paperContent.trim() || undefined,
          website: website.trim() || undefined,
          github: github.trim() || undefined,
          twitter: twitter.trim() || undefined,
          discord: discord.trim() || undefined,
        });

        if (response.success && response.data?.project) {
          savedProjectId = response.data.project.id;
          setProjectId(savedProjectId);
        }
      }

      // Publish if requested
      if (publish && savedProjectId) {
        await publishProject(savedProjectId);
        setStatus('published');
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!projectId) {
      // Save first, then publish
      await handleSave(true);
    } else {
      setSaving(true);
      try {
        await publishProject(projectId);
        setStatus('published');
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to publish');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleUnpublish = async () => {
    if (!projectId) return;
    
    setSaving(true);
    try {
      await unpublishProject(projectId);
      setStatus('draft');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unpublish');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId) return;
    if (!confirm('Are you sure you want to delete this project? This cannot be undone.')) return;

    setSaving(true);
    try {
      await deleteProject(projectId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setSaving(false);
    }
  };

  const handleMediaAdd = async (newMedia: { mediaType: MediaType; url: string; cloudflareKey: string }, category: 'logo' | 'banner' | 'media' = 'media') => {
    if (!projectId) {
      // Need to save project first
      setError('Please save the project first before adding media');
      return;
    }

    try {
      const response = await addProjectMedia(projectId, { ...newMedia, category });
      if (response.success && response.data?.media) {
        if (category === 'logo') {
          setLogo(response.data.media);
        } else if (category === 'banner') {
          setBanner(response.data.media);
        } else {
          setMedia([...media, response.data.media]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add media');
    }
  };

  const handleMediaRemove = async (mediaId: string, category?: 'logo' | 'banner' | 'media') => {
    if (!projectId) return;

    try {
      await deleteProjectMedia(projectId, mediaId);
      if (category === 'logo') {
        setLogo(null);
      } else if (category === 'banner') {
        setBanner(null);
      } else {
        setMedia(media.filter(m => m.id !== mediaId));
      }
    } catch (err) {
      console.error('Failed to remove media:', err);
    }
  };

  if (!isOpen) return null;

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(8px)',
    zIndex: isMobile ? 300 : 60, // Higher on mobile to be above bottom nav
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: isMobile ? 301 : 70, // Higher on mobile to be above bottom nav
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.98)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    ...(isMobile ? {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '20px 20px 0 0',
      maxHeight: '95vh',
    } : {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '600px',
      maxHeight: '85vh',
      borderRadius: '20px',
    }),
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.7)',
    marginBottom: '8px',
  };

  const tabStyle = (isActive: boolean): CSSProperties => ({
    flex: 1,
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: 600,
    color: isActive ? 'white' : 'rgba(255,255,255,0.5)',
    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle}>
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {isMobile && (
            <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 16px' }} />
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9945FF, #14F195)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}>
                {isEditing ? '✏️' : '🚀'}
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>
                  {isEditing ? 'Edit Project' : 'New Project'}
                </h2>
                {status === 'published' && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(20, 241, 149, 0.2)',
                    color: '#14F195',
                  }}>
                    PUBLISHED
                  </span>
                )}
              </div>
            </div>
            
            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '12px' }}>
            <button onClick={withClickSound(() => setActiveTab('details'))} style={tabStyle(activeTab === 'details')}>
              Details
            </button>
            <button onClick={withClickSound(() => setActiveTab('media'))} style={tabStyle(activeTab === 'media')}>
              Media
            </button>
            <button onClick={withClickSound(() => setActiveTab('paper'))} style={tabStyle(activeTab === 'paper')}>
              Paper
            </button>
            <button onClick={withClickSound(() => setActiveTab('links'))} style={tabStyle(activeTab === 'links')}>
              Links
            </button>
            <button onClick={withClickSound(() => setActiveTab('achievements'))} style={tabStyle(activeTab === 'achievements')}>
              Achievements
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px', paddingBottom: isMobile ? '100px' : '20px' }}>
          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              color: '#f87171',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}

          {activeTab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Team selection removed in projects-only mode */}

              {/* Name */}
              <div>
                <label style={labelStyle}>Project Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Awesome Project"
                  style={inputStyle}
                />
              </div>

              {/* Tagline */}
              <div>
                <label style={labelStyle}>Tagline</label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="A short catchy description"
                  style={inputStyle}
                />
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your project in detail..."
                  rows={5}
                  style={{ ...inputStyle, resize: 'none' }}
                />
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {!projectId && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '12px',
                  color: '#ffc107',
                  fontSize: '13px',
                }}>
                  Save the project first to upload media
                </div>
              )}
              
              {/* Logo Upload */}
              <div>
                <label style={{ ...labelStyle, marginBottom: '8px', display: 'block' }}>Project Logo</label>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                  Square image recommended (e.g., 512x512px)
                </p>
                {logo ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={logo.url}
                      alt="Logo"
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    />
                    <button
                      onClick={() => handleMediaRemove(logo.id, 'logo')}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#ef4444',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <MediaUploader
                    media={[]}
                    onMediaAdd={(m) => handleMediaAdd(m, 'logo')}
                    onMediaRemove={() => {}}
                    disabled={!projectId}
                    singleUpload
                  />
                )}
              </div>

              {/* Banner Upload */}
              <div>
                <label style={{ ...labelStyle, marginBottom: '8px', display: 'block' }}>Banner Image</label>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                  Wide image for project header (e.g., 1920x600px)
                </p>
                {banner ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={banner.url}
                      alt="Banner"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    />
                    <button
                      onClick={() => handleMediaRemove(banner.id, 'banner')}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#ef4444',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <MediaUploader
                    media={[]}
                    onMediaAdd={(m) => handleMediaAdd(m, 'banner')}
                    onMediaRemove={() => {}}
                    disabled={!projectId}
                    singleUpload
                  />
                )}
              </div>

              {/* Media Gallery */}
              <div>
                <label style={{ ...labelStyle, marginBottom: '8px', display: 'block' }}>Media Gallery</label>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                  Additional images and videos for your project
                </p>
                <MediaUploader
                  media={media}
                  onMediaAdd={(m) => handleMediaAdd(m, 'media')}
                  onMediaRemove={handleMediaRemove}
                  disabled={!projectId}
                />
              </div>
            </div>
          )}

          {activeTab === 'paper' && (
            <div>
              <label style={labelStyle}>Technical Paper / Documentation</label>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>
                Add technical details, whitepaper content, or documentation (Markdown supported)
              </p>
              <textarea
                value={paperContent}
                onChange={(e) => setPaperContent(e.target.value)}
                placeholder="# Overview\n\nDescribe the technical aspects of your project...\n\n## Architecture\n\n..."
                rows={15}
                style={{ ...inputStyle, resize: 'none', fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>
          )}

          {activeTab === 'links' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Website</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://myproject.com"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>GitHub</label>
                <input
                  type="url"
                  value={github}
                  onChange={(e) => setGithub(e.target.value)}
                  placeholder="https://github.com/org/repo"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>X (Twitter)</label>
                <input
                  type="text"
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                  placeholder="@projecthandle"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Discord</label>
                <input
                  type="url"
                  value={discord}
                  onChange={(e) => setDiscord(e.target.value)}
                  placeholder="https://discord.gg/invite"
                  style={inputStyle}
                />
              </div>

              {/* X Verification Section */}
              {projectId && (
                <div style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                      <label style={{ ...labelStyle, marginBottom: '4px' }}>X (Twitter) Verification</label>
                      {xVerifiedAt ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <svg style={{ width: '16px', height: '16px', color: '#14F195' }} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                          <span style={{ fontSize: '13px', color: '#14F195', fontWeight: 500 }}>
                            Verified @{xUsername || editProject?.xUsername || 'unknown'}
                          </span>
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                            {new Date(xVerifiedAt).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
                          Verify your project's X account to add credibility
                        </p>
                      )}
                    </div>
                    {!xVerifiedAt && (
                      <button
                        onClick={withClickSound(async () => {
                          if (!projectId) return;
                          setVerifyingX(true);
                          try {
                            const response = await getProjectVerificationUrl(projectId);
                            if (response.success && response.data) {
                              const popup = window.open(
                                response.data.url,
                                'x-verification',
                                'width=600,height=700,scrollbars=yes,resizable=yes'
                              );

                              const handleMessage = async (event: MessageEvent) => {
                                if (event.origin !== window.location.origin) return;
                                if (event.data.type === 'x-verification-callback') {
                                  window.removeEventListener('message', handleMessage);
                                  popup?.close();
                                  
                                  try {
                                    const verifyResponse = await verifyProjectX(projectId, event.data.code, event.data.state);
                                    if (verifyResponse.success && verifyResponse.data?.project) {
                                      setXVerifiedAt(verifyResponse.data.project.xVerifiedAt);
                                      setXUsername(verifyResponse.data.project.xUsername);
                                    }
                                  } catch (err) {
                                    console.error('Verification failed:', err);
                                    setError('Verification failed. Please try again.');
                                  } finally {
                                    setVerifyingX(false);
                                  }
                                }
                              };
                              window.addEventListener('message', handleMessage);
                            }
                          } catch (err) {
                            console.error('Failed to start verification:', err);
                            setError('Failed to start verification. Please try again.');
                            setVerifyingX(false);
                          }
                        })}
                        disabled={verifyingX}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '10px',
                          background: verifyingX ? 'rgba(255,255,255,0.1)' : 'rgba(29, 161, 242, 0.2)',
                          border: '1px solid rgba(29, 161, 242, 0.5)',
                          color: verifyingX ? 'rgba(255,255,255,0.3)' : '#1DA1F2',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: verifyingX ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {verifyingX ? 'Verifying...' : 'Verify X Account'}
                        <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Members tab removed in projects-only mode */}

          {activeTab === 'achievements' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'white' }}>Your Achievements</h3>
                <button
                  onClick={withClickSound(() => {
                    setEditingAchievement(null);
                    setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                    setShowAchievementForm(true);
                  })}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    background: 'rgba(20, 241, 149, 0.2)',
                    border: '1px solid rgba(20, 241, 149, 0.3)',
                    color: '#14F195',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  + Add Achievement
                </button>
              </div>

              {showAchievementForm && (
                <div style={{ padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '16px' }}>
                    {editingAchievement ? 'Edit Achievement' : 'New Achievement'}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Type *</label>
                      <select
                        value={achievementForm.type}
                        onChange={(e) => setAchievementForm({ ...achievementForm, type: e.target.value as AchievementType })}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="hackathon_winner">Hackathon Winner</option>
                        <option value="grant_winner">Grant Winner</option>
                        <option value="conference_speaker">Conference Speaker</option>
                        <option value="publication">Publication</option>
                        <option value="award">Award</option>
                        <option value="certification">Certification</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Title *</label>
                      <input
                        type="text"
                        value={achievementForm.title}
                        onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })}
                        placeholder="e.g., Solana Hackathon Winner 2024"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Description</label>
                      <textarea
                        value={achievementForm.description}
                        onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                        placeholder="Describe your achievement..."
                        rows={3}
                        style={{ ...inputStyle, resize: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Date</label>
                      <input
                        type="date"
                        value={achievementForm.date}
                        onChange={(e) => setAchievementForm({ ...achievementForm, date: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Issuer</label>
                      <input
                        type="text"
                        value={achievementForm.issuer}
                        onChange={(e) => setAchievementForm({ ...achievementForm, issuer: e.target.value })}
                        placeholder="e.g., Solana Foundation"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Proof URL (optional)</label>
                      <input
                        type="url"
                        value={achievementForm.proofUrl}
                        onChange={(e) => setAchievementForm({ ...achievementForm, proofUrl: e.target.value })}
                        placeholder="https://... (optional)"
                        style={inputStyle}
                      />
                    </div>
                    {achievementForm.type === 'grant_winner' && (
                      <div>
                        <label style={labelStyle}>Superteam</label>
                        <select
                          value={achievementForm.superteamId}
                          onChange={(e) => setAchievementForm({ ...achievementForm, superteamId: e.target.value })}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          <option value="">Select a Superteam (optional)</option>
                          {superteams.map((team) => (
                            <option key={team.id} value={team.id}>{team.name} ({team.country})</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                          Select the Superteam this grant came from
                        </p>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={withClickSound(async () => {
                          if (!user) {
                            setError('You must be logged in to save achievements');
                            return;
                          }
                          
                          const trimmedTitle = achievementForm.title?.trim();
                          if (!trimmedTitle || trimmedTitle.length === 0) {
                            setError('Title is required');
                            return;
                          }

                          setSaving(true);
                          setError(null);

                          try {
                            if (editingAchievement) {
                              const response = await updateAchievement(editingAchievement.id, achievementForm);
                              if (response.success) {
                                // Optimistically update the list
                                const updatedAchievement = response.data!.achievement;
                                setAchievements(prev => prev.map(a => a.id === editingAchievement.id ? updatedAchievement : a));
                                setEditingAchievement(null);
                                setShowAchievementForm(false);
                                setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                                // Don't refetch - we already have the updated achievement
                              } else {
                                setError(response.error || 'Failed to update achievement');
                              }
                            } else {
                              const response = await createAchievement(achievementForm);
                              if (response.success) {
                                setAchievements([...achievements, response.data!.achievement]);
                                setShowAchievementForm(false);
                                setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                              } else {
                                setError(response.error || 'Failed to create achievement');
                              }
                            }
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to save achievement');
                          } finally {
                            setSaving(false);
                          }
                        })}
                        disabled={saving || !achievementForm.title.trim()}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '10px',
                          background: achievementForm.title.trim() ? 'linear-gradient(135deg, #14F195, #00C2FF)' : 'rgba(255,255,255,0.1)',
                          border: 'none',
                          color: achievementForm.title.trim() ? 'black' : 'rgba(255,255,255,0.3)',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: saving || !achievementForm.title.trim() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {saving ? 'Saving...' : editingAchievement ? 'Update' : 'Create'}
                      </button>
                      <button
                        onClick={withClickSound(() => {
                          setShowAchievementForm(false);
                          setEditingAchievement(null);
                          setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                        })}
                        style={{
                          padding: '12px 20px',
                          borderRadius: '10px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {loadingAchievements ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading achievements...</div>
              ) : achievements.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  No achievements yet. Add your first achievement to showcase your accomplishments!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {achievements.map(achievement => (
                    <div
                      key={achievement.id}
                      style={{
                        padding: '16px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              background: 'rgba(20, 241, 149, 0.1)',
                              color: '#14F195',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}>
                              {achievement.type.replace('_', ' ')}
                            </span>
                            {achievement.type === 'grant_winner' && (
                              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>💰 Grant</span>
                            )}
                          </div>
                          <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '4px' }}>
                            {achievement.title}
                          </h4>
                          {achievement.description && (
                            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                              {achievement.description}
                            </p>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            {achievement.issuer && <span>Issued by: {achievement.issuer}</span>}
                            {achievement.superteamId && (
                              <span>
                                Superteam: {superteams.find(st => st.id === achievement.superteamId)?.name || achievement.superteamId}
                              </span>
                            )}
                            {achievement.date && <span>Date: {new Date(achievement.date).toLocaleDateString()}</span>}
                            {achievement.proofUrl && (
                              <a href={achievement.proofUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#14F195', textDecoration: 'none' }}>
                                View Proof →
                              </a>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={withClickSound(() => {
                              setEditingAchievement(achievement);
                              setAchievementForm({
                                type: achievement.type,
                                title: achievement.title,
                                description: achievement.description || '',
                                date: achievement.date || '',
                                issuer: achievement.issuer || '',
                                proofUrl: achievement.proofUrl || '',
                                superteamId: achievement.superteamId || '',
                              });
                              setShowAchievementForm(true);
                            })}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={withClickSound(async () => {
                              if (!confirm('Are you sure you want to delete this achievement?')) return;

                              setSaving(true);
                              setError(null);

                              try {
                                const response = await deleteAchievement(achievement.id);
                                if (response.success) {
                                  // Optimistically update the list
                                  setAchievements(prev => prev.filter(a => a.id !== achievement.id));
                                  // Don't refetch - we already removed it
                                } else {
                                  setError(response.error || 'Failed to delete achievement');
                                }
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Failed to delete achievement');
                              } finally {
                                setSaving(false);
                              }
                            })}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              color: '#f87171',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0,
          padding: '20px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          {isEditing && (
            <button
              onClick={withClickSound(handleDelete)}
              disabled={saving}
              style={{
                padding: '12px 20px',
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Delete
            </button>
          )}

          <div style={{ flex: 1 }} />

          {status === 'published' ? (
            <button
              onClick={withClickSound(handleUnpublish)}
              disabled={saving}
              style={{
                padding: '12px 20px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Unpublish
            </button>
          ) : null}

          <button
            onClick={withClickSound(() => handleSave(false))}
            disabled={saving || !name.trim()}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 600,
              fontSize: '14px',
              cursor: saving || !name.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>

          {status !== 'published' && (
            <button
              onClick={withClickSound(handlePublish)}
              disabled={saving || !name.trim()}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                background: !name.trim() ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #14F195, #0da672)',
                border: 'none',
                color: !name.trim() ? 'rgba(255,255,255,0.3)' : 'black',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving || !name.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Publishing...' : 'Publish'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
